/**
 * Pure mapping from voice status/mode to which controls and messages `VoicePanel.tsx` shows (T6). Kept
 * separate from the component and the hook so the decision table is unit-testable without React or timers —
 * see packages/client/test/voice.test.ts.
 */
import type { VoiceConnectionStatus, VoiceMode } from '@momentum/shared';
import { strings } from '../shell/strings.ts';

export interface VoiceUiStateInput {
  mode: VoiceMode;
  status: VoiceConnectionStatus;
  /** Provider-reported explanation, when there is one; falls back to a generic hint per status. */
  detail: string | null;
  muted: boolean;
  /** Whether this connection has ever successfully unmuted before (hides the one-time "Enable microphone" CTA after). */
  everUnmuted: boolean;
  /** False for spectators/presenters: they can watch the indicators but never publish. */
  canPublish: boolean;
}

export interface VoiceUiState {
  /** Nothing to render at all — voice is off for this room. */
  isOff: boolean;
  statusLabel: string;
  statusDetail: string | null;
  showRetry: boolean;
  showMuteButton: boolean;
  muteButtonLabel: string;
  muteButtonPressed: boolean;
  /** The prominent one-time "Enable microphone" call to action for remote mode. */
  showEnableMicButton: boolean;
  showListeningOnlyNote: boolean;
}

const STATUS_LABELS: Record<VoiceConnectionStatus, string> = {
  idle: strings.voice.statusIdle,
  connecting: strings.voice.statusConnecting,
  connected: strings.voice.statusConnected,
  reconnecting: strings.voice.statusReconnecting,
  permission_denied: strings.voice.statusPermissionDenied,
  device_error: strings.voice.statusDeviceError,
  failed: strings.voice.statusFailed,
  disconnected: strings.voice.statusDisconnected,
};

const DEFAULT_DETAIL: Partial<Record<VoiceConnectionStatus, string>> = {
  permission_denied: strings.voice.permissionDeniedHint,
  device_error: strings.voice.deviceErrorHint,
};

const RETRYABLE_STATUSES: ReadonlySet<VoiceConnectionStatus> = new Set([
  'disconnected',
  'failed',
  'device_error',
  'permission_denied',
]);

export function voiceUiState(input: VoiceUiStateInput): VoiceUiState {
  if (input.mode === 'off') {
    return {
      isOff: true,
      statusLabel: strings.voice.modeOff,
      statusDetail: null,
      showRetry: false,
      showMuteButton: false,
      muteButtonLabel: '',
      muteButtonPressed: false,
      showEnableMicButton: false,
      showListeningOnlyNote: false,
    };
  }

  const showMuteButton = input.canPublish && input.status === 'connected';
  const showEnableMicButton =
    input.canPublish && input.mode === 'remote' && input.status === 'connected' && input.muted && !input.everUnmuted;

  return {
    isOff: false,
    statusLabel: STATUS_LABELS[input.status],
    statusDetail: input.detail ?? DEFAULT_DETAIL[input.status] ?? null,
    showRetry: RETRYABLE_STATUSES.has(input.status),
    showMuteButton,
    muteButtonLabel: input.muted ? strings.voice.unmute : strings.voice.mute,
    muteButtonPressed: !input.muted,
    showEnableMicButton,
    showListeningOnlyNote: !input.canPublish && (input.status === 'connected' || input.status === 'reconnecting'),
  };
}
